import {
	AfterViewInit,
	ContentChildren,
	Directive,
	ElementRef,
	OnDestroy,
	QueryList,
	Renderer2,
} from '@angular/core';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { Subscription } from 'rxjs';

@Directive({ selector: 'mat-tab-group[appTabAutomationId]' })
export class TabAutomationIdDirective implements AfterViewInit, OnDestroy {
	@ContentChildren(MatTab, { read: ElementRef })
	tabHosts!: QueryList<ElementRef>;

	private tabsChangeSubscription?: Subscription;

	constructor(
		private readonly tabGroup: MatTabGroup,
		private readonly el: ElementRef,
		private readonly renderer: Renderer2
	) {}

	ngAfterViewInit(): void {
		// Add automation IDs initially
		setTimeout(() => {
			this.addAutomationIds();
		});

		// Subscribe to tab changes to handle dynamic tabs (e.g., with *ngIf)
		this.tabsChangeSubscription = this.tabGroup._tabs.changes.subscribe(() => {
			setTimeout(() => {
				this.addAutomationIds();
			});
		});
	}

	ngOnDestroy(): void {
		this.tabsChangeSubscription?.unsubscribe();
	}

	private formatAutomationIdTabStrings(str: string): string {
		return str.trim().toLowerCase().split(' ').join('-');
	}

	private resolveTabAutomationId(
		tab: MatTab | undefined,
		tabHost: Element | undefined,
		fallbackLabelText: string
	): string {
		const explicitId = tabHost?.getAttribute('data-automation-id');
		if (explicitId) {
			return explicitId;
		}

		if (tab?.ariaLabel) {
			return `${this.formatAutomationIdTabStrings(tab.ariaLabel)}-tab`;
		}

		const ariaLabel = tabHost?.getAttribute('aria-label');
		if (ariaLabel) {
			return `${this.formatAutomationIdTabStrings(ariaLabel)}-tab`;
		}

		return `${this.formatAutomationIdTabStrings(fallbackLabelText)}-tab`;
	}

	private addAutomationIds(): void {
		const tabHeaders = this.el.nativeElement.querySelectorAll('.mat-mdc-tab');
		const tabLabelText = this.el.nativeElement.querySelectorAll(
			'.mdc-tab__text-label'
		);
		const tabs = this.tabGroup._tabs.toArray();
		const tabHosts = this.tabHosts?.toArray() ?? [];

		tabs.forEach((tab, index) => {
			if (!tabHeaders[index]) {
				return;
			}

			const labelText = tabLabelText[index]?.textContent?.trim();
			const fallbackLabel = labelText || tab.textLabel || `tab-${index}`;
			const automationId = this.resolveTabAutomationId(
				tab,
				tabHosts[index]?.nativeElement,
				fallbackLabel
			);
			this.renderer.setAttribute(
				tabHeaders[index],
				'data-automation-id',
				automationId
			);
		});
	}
}
